import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiCalendar, FiChevronLeft, FiChevronRight, FiClock, FiMapPin, FiUsers, FiArrowRight, FiX, FiZap, FiNavigation } from 'react-icons/fi';
import { getSchedule, scheduleJob, getAuthToken, getCrews, assignJobCrew, getTeamMembers } from '../services/api';
import { API_URL } from '../config';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ScheduleJobPreviewModal from '../components/ScheduleJobPreviewModal';

const PRIORITY_CONFIG = {
  Urgent: { color: '#dc2626', bg: '#fef2f2', border: '#fecaca', label: 'Urgent', sort: 0 },
  High:   { color: '#ea580c', bg: '#fff7ed', border: '#fed7aa', label: 'High', sort: 1 },
  Normal: { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', label: 'Normal', sort: 2 },
  Low:    { color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', label: 'Low', sort: 3 }
};

const FIELD_WORK_STATUSES_SET = new Set(['Pending', 'Scheduled', 'In Progress - Initial Field Work', 'In Progress - Topo', 'In Progress - Additional Field Work Required', 'Final Mark Up / Stake Lines']);

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  const hour = parseInt(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h12}:${m} ${ampm}`;
}

function PriorityBadge({ priority, small }) {
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.Normal;
  return (
    <span style={{
      display: 'inline-block',
      padding: small ? '1px 6px' : '2px 8px',
      borderRadius: '4px',
      fontSize: small ? '10px' : '11px',
      fontWeight: '700',
      color: config.color,
      backgroundColor: config.bg,
      border: `1px solid ${config.border}`,
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }}>
      {config.label}
    </span>
  );
}

function JobCard({ job, onSchedule, onPriorityChange, showDate, isMobile, onDragStart, onEnRoute, onPreview, crews, soloOptions = [], onCrewChange }) {
  const config = PRIORITY_CONFIG[job.priority] || PRIORITY_CONFIG.Normal;

  return (
    <div
      draggable={!isMobile && !!onDragStart}
      onDragStart={(e) => {
        if (onDragStart) {
          e.dataTransfer.setData('text/plain', JSON.stringify({ jobId: job.id }));
          e.currentTarget.style.opacity = '0.5';
          onDragStart(job.id);
        }
      }}
      onDragEnd={(e) => { e.currentTarget.style.opacity = '1'; }}
      onClick={onPreview ? () => onPreview(job.id) : undefined}
      style={{
        backgroundColor: 'white',
        border: `2px solid ${config.border}`,
        borderLeft: `4px solid ${config.color}`,
        borderRadius: '8px',
        padding: isMobile ? '10px' : '12px',
        marginBottom: '8px',
        fontSize: '13px',
        cursor: onPreview ? 'pointer' : (!isMobile && onDragStart ? 'grab' : 'default')
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Link to={`/jobs/${job.id}`} state={{ from: '/schedule', label: 'Schedule' }} onClick={(e) => e.stopPropagation()} style={{ fontWeight: '700', color: '#1f2937', textDecoration: 'none', fontSize: '14px' }}>
            {job.job_number}
          </Link>
          {job.client_name && <div style={{ color: '#6b7280', fontSize: '12px' }}>{job.client_name}</div>}
        </div>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0 }}>
          <PriorityBadge priority={job.priority} small />
          <select
            value={job.priority || 'Normal'}
            onChange={(e) => onPriorityChange(job.id, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{
              padding: '2px 4px', fontSize: '11px', border: '1px solid #ddd',
              borderRadius: '4px', cursor: 'pointer', backgroundColor: 'white'
            }}
          >
            <option value="Urgent">Urgent</option>
            <option value="High">High</option>
            <option value="Normal">Normal</option>
            <option value="Low">Low</option>
          </select>
        </div>
      </div>

      {job.property_address && (
        <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <FiMapPin size={11} /> {job.property_address}
        </div>
      )}

      {job.scheduled_time && (
        <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <FiClock size={11} /> {formatTime(job.scheduled_time)}
        </div>
      )}

      {job.team_members && job.team_members.length > 0 && (
        <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <FiUsers size={11} /> {job.team_members.join(', ')}
        </div>
      )}

      {/* Crew assignment. This is what puts the job in a column on the Command Center
          board — until a crew is set, the job sits in the board's unassigned strip. */}
      {crews && crews.length > 0 && onCrewChange && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px' }}
        >
          <span
            title={job.crew_name ? `Assigned to ${job.crew_name}` : 'No crew assigned'}
            style={{
              width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0,
              backgroundColor: job.crew_color || '#d1d5db',
              border: job.crew_color ? 'none' : '1px dashed #9ca3af',
            }}
          />
          {/* Value is prefixed so one <select> can express both kinds of assignment:
              a crew, or one person working alone. They're mutually exclusive server-side. */}
          <select
            value={job.crew_id ? `c${job.crew_id}` : (job.solo_member_id ? `m${job.solo_member_id}` : '')}
            onChange={(e) => onCrewChange(job.id, e.target.value)}
            style={{
              flex: 1, minWidth: 0, padding: '3px 5px', fontSize: '11px',
              border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer',
              backgroundColor: 'white',
              color: (job.crew_id || job.solo_member_id) ? '#1f2937' : '#9ca3af',
              fontWeight: (job.crew_id || job.solo_member_id) ? 600 : 400,
            }}
          >
            <option value="">— no crew —</option>
            <optgroup label="Crews">
              {crews.map((c) => <option key={c.id} value={`c${c.id}`}>{c.name}</option>)}
            </optgroup>
            {soloOptions.length > 0 && (
              <optgroup label="One person">
                {soloOptions.map((m) => <option key={m.id} value={`m${m.id}`}>{m.name}</option>)}
              </optgroup>
            )}
          </select>
        </div>
      )}

      {(() => {
        // Show Scheduled and In Progress work orders on the schedule (not Pending / Completed)
        const scheduledWOs = (job.work_orders || []).filter(wo => wo.status === 'Scheduled' || wo.status === 'In Progress');
        if (scheduledWOs.length === 0) return null;
        return (
          <div style={{ marginTop: '6px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 8px' }}>
            <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#94a3b8', marginBottom: '3px' }}>
              Work orders · {scheduledWOs.length}
            </div>
            {scheduledWOs.map(wo => (
              <div key={wo.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#334155', padding: '1px 0' }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: wo.status === 'In Progress' ? '#16a34a' : '#60a5fa', flexShrink: 0 }} />
                <span style={{ fontWeight: 600, flexShrink: 0 }}>{wo.order_number || `WO-${wo.id}`}</span>
                {wo.description && (
                  <span style={{ color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>· {wo.description}</span>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      {job.status && job.status !== 'Pending' && job.status !== 'Scheduled' && (
        <div style={{
          marginTop: '4px', fontSize: '11px', color: '#7c3aed',
          fontWeight: '600'
        }}>
          {job.status}
        </div>
      )}

      {onEnRoute && (
        <button
          onClick={(e) => { e.stopPropagation(); onEnRoute(job); }}
          style={{
            marginTop: '6px', padding: '5px 10px', fontSize: '11px',
            backgroundColor: '#16a34a', color: 'white', border: 'none',
            borderRadius: '6px', cursor: 'pointer', fontWeight: '600',
            display: 'flex', alignItems: 'center', gap: '4px', width: '100%',
            justifyContent: 'center',
          }}
        >
          🚗 En Route — Notify Client
        </button>
      )}

      {showDate && (
        <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
          {onSchedule && (
            <button
              onClick={() => onSchedule(job.id)}
              style={{
                padding: '4px 10px', fontSize: '11px', backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe', borderRadius: '4px', cursor: 'pointer',
                color: '#2563eb', display: 'flex', alignItems: 'center', gap: '4px'
              }}
            >
              <FiCalendar size={11} /> Schedule
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Schedule() {
  const { user } = useAuth();
  const canManagePayroll = user?.can_manage_payroll || false;
  const canEditSchedule = user?.can_edit_schedule || canManagePayroll;
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [schedule, setSchedule] = useState({ unscheduled: [], scheduled: {} });
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [schedulingJob, setSchedulingJob] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ date: '', time: '' });
  // Default the mobile day tab to today (Mon=0..Fri=4); weekends fall back to Monday
  const [mobileDay, setMobileDay] = useState(() => {
    const dow = new Date().getDay(); // 0=Sun..6=Sat
    return (dow >= 1 && dow <= 5) ? dow - 1 : 0;
  });
  const [crews, setCrews] = useState([]);
  const [team, setTeam] = useState([]);
  const [nearbyJobs, setNearbyJobs] = useState([]);
  const [showNearby, setShowNearby] = useState(null);
  const [nearbyPickerDate, setNearbyPickerDate] = useState(null); // date string — show job picker
  const [smartScheduling, setSmartScheduling] = useState(false);
  const [draggingJobId, setDraggingJobId] = useState(null);
  const [draggingWO, setDraggingWO] = useState(null);   // { id, job_id } — a work order being dragged to a new date
  const [dragOverDate, setDragOverDate] = useState(null);
  const [previewJobId, setPreviewJobId] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const formatLocal = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      const startDate = formatLocal(weekStart);
      const endD = new Date(weekStart);
      endD.setDate(endD.getDate() + 4);
      const endDate = formatLocal(endD);
      const response = await getSchedule(startDate, endDate);
      setSchedule(response.data);
    } catch (error) {
      console.error('Error loading schedule:', error);
    }
    setLoading(false);
  }, [weekStart]);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  // Crews and the roster change rarely, so these load once rather than with every
  // week change.
  useEffect(() => {
    getCrews().then((r) => setCrews(r.data || [])).catch(() => setCrews([]));
    getTeamMembers()
      .then((r) => setTeam(r.data?.team_members || r.data || []))
      .catch(() => setTeam([]));
  }, []);

  // Everyone who could be sent out alone — the WHOLE active roster, not just people on
  // a crew. The owner and the CAD techs go to site too (Paul has 7 solo outings in the
  // last 120 days), and building this from crew membership silently excluded them.
  const soloOptions = React.useMemo(
    () => team
      .filter((m) => m.is_active !== false)
      .map((m) => ({ id: m.id, name: m.name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [team],
  );

  const handleCrewChange = useCallback(async (jobId, value) => {
    // value is '' | 'c<crewId>' | 'm<memberId>' — one control, two kinds of assignment.
    const isCrew = value.startsWith('c');
    const isSolo = value.startsWith('m');
    const id = value ? parseInt(value.slice(1), 10) : null;
    const crew = isCrew ? crews.find((c) => c.id === id) : null;
    const person = isSolo ? soloOptions.find((m) => m.id === id) : null;

    // Optimistic: the picker should feel instant, and a failure is recoverable by
    // simply picking again. Reloading the whole week on every change would make
    // assigning a day's worth of jobs feel like wading.
    setSchedule((prev) => {
      const next = { ...prev, scheduled: { ...prev.scheduled } };
      for (const [date, jobs] of Object.entries(next.scheduled)) {
        next.scheduled[date] = jobs.map((j) => (
          j.id === jobId
            ? {
              ...j,
              crew_id: isCrew ? id : null,
              crew_name: crew?.name || null,
              crew_color: isCrew ? (crew?.color || null) : (isSolo ? '#0891b2' : null),
              solo_member_id: isSolo ? id : null,
              solo_member_name: person?.name || null,
            }
            : j
        ));
      }
      return next;
    });
    try {
      await assignJobCrew(jobId, isCrew ? id : null, isSolo ? id : null);
    } catch (e) {
      console.error('Could not assign crew:', e);
      loadSchedule();   // put the truth back on screen
    }
  }, [crews, soloOptions, loadSchedule]);

  const getDayDates = () => {
    return Array.from({ length: 5 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    });
  };

  const handlePriorityChange = async (jobId, priority) => {
    try {
      await scheduleJob(jobId, { priority });
      loadSchedule();
    } catch (error) {
      alert('Failed to update priority');
    }
  };

  const getAuthConfig = async () => {
    const token = await getAuthToken();
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  const handleSmartSchedule = async () => {
    if (!window.confirm('This will auto-schedule all unscheduled jobs, grouping nearby jobs on the same days. Continue?')) return;
    setSmartScheduling(true);
    try {
      const config = await getAuthConfig();
      const response = await axios.post(`${API_URL}/schedule/smart`, {}, config);
      alert(response.data.message);
      loadSchedule();
    } catch (error) {
      alert(error.response?.data?.error || 'Smart schedule failed');
    }
    setSmartScheduling(false);
  };

  const loadNearbyJobs = async (date, lat, lng) => {
    try {
      const config = await getAuthConfig();
      let url = `${API_URL}/schedule/nearby?date=${date}`;
      if (lat && lng) url += `&lat=${lat}&lng=${lng}`;
      const response = await axios.get(url, config);
      setNearbyJobs(response.data.nearby || []);
      setNearbyPickerDate(null);
      setShowNearby(date);
    } catch (error) {
      console.error('Failed to load nearby jobs:', error);
    }
  };

  const showWarnings = (response) => {
    if (response.data?.warnings?.length > 0) {
      alert('Warning:\n' + response.data.warnings.join('\n'));
    }
  };

  const handleDrop = async (date) => {
    setDragOverDate(null);
    // A work order being rescheduled to a different day.
    if (draggingWO) {
      try {
        const config = await getAuthConfig();
        await axios.put(`${API_URL}/jobs/${draggingWO.job_id}/work-orders/${draggingWO.id}`, { scheduled_date: date }, config);
        loadSchedule();
      } catch (error) {
        alert('Failed to move the work order.');
      }
      setDraggingWO(null);
      return;
    }
    if (!draggingJobId) return;
    try {
      const response = await scheduleJob(draggingJobId, { scheduled_date: date });
      showWarnings(response);
      loadSchedule();
    } catch (error) {
      alert('Failed to move job');
    }
    setDraggingJobId(null);
  };

  const handleScheduleSubmit = async () => {
    if (!schedulingJob || !scheduleForm.date) return;
    try {
      const response = await scheduleJob(schedulingJob, {
        scheduled_date: scheduleForm.date,
        scheduled_time: scheduleForm.time || null
      });
      showWarnings(response);
      setSchedulingJob(null);
      loadSchedule();
    } catch (error) {
      alert('Failed to schedule job');
    }
  };

  const handleUnschedule = async (jobId) => {
    try {
      await scheduleJob(jobId, { scheduled_date: null, scheduled_time: null });
      loadSchedule();
    } catch (error) {
      alert('Failed to unschedule job');
    }
  };

  const handleEnRoute = async (job) => {
    if (!window.confirm(`Text & email the client that the crew is headed to ${job.job_number}?`)) return;
    try {
      const config = await (async () => {
        const token = await getAuthToken();
        return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
      })();
      // Server sends the SMS (company Twilio number) + email and logs it to the job thread
      const res = await axios.post(`${API_URL}/jobs/${job.id}/portal/en-route`, {}, config);
      alert(res.data?.message || 'Client notified!');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to notify client');
    }
  };

  // En route for a specific phase work order — notifies only that phase's client/contacts
  // (server-side SMS + email + phase portal note; logged to the job's text thread).
  const handleWoEnRoute = async (wo) => {
    if (!window.confirm(`Notify the ${wo.phase_name || 'phase'} client for ${wo.job_number} that the crew is on the way?`)) return;
    try {
      const config = await (async () => {
        const token = await getAuthToken();
        return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
      })();
      const res = await axios.post(`${API_URL}/jobs/${wo.job_id}/portal/en-route`, { phase_id: wo.phase_id }, config);
      alert(res.data?.message || 'Client notified!');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to notify client');
    }
  };

  const handleQuickSchedule = async (jobId, dateStr) => {
    try {
      const response = await scheduleJob(jobId, { scheduled_date: dateStr });
      showWarnings(response);
      loadSchedule();
    } catch (error) {
      alert('Failed to schedule job');
    }
  };

  const prevWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); };
  const nextWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); };
  const thisWeek = () => setWeekStart(getMonday(new Date()));

  const dayDates = getDayDates();
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <div style={{ textAlign: 'center', color: '#6b7280' }}>
          <FiCalendar size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p>Loading schedule...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '15px' : '24px' }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '20px', flexWrap: 'wrap', gap: '10px'
      }}>
        <h1 style={{ margin: 0, fontSize: isMobile ? '20px' : '28px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FiCalendar /> Schedule
        </h1>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={prevWeek} style={{
            padding: '8px 12px', backgroundColor: 'white', border: '1px solid #ddd',
            borderRadius: '6px', cursor: 'pointer'
          }}>
            <FiChevronLeft />
          </button>
          <button onClick={thisWeek} style={{
            padding: '8px 16px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe',
            borderRadius: '6px', cursor: 'pointer', color: '#2563eb', fontWeight: '600', fontSize: '14px'
          }}>
            This Week
          </button>
          <button onClick={nextWeek} style={{
            padding: '8px 12px', backgroundColor: 'white', border: '1px solid #ddd',
            borderRadius: '6px', cursor: 'pointer'
          }}>
            <FiChevronRight />
          </button>
        </div>
      </div>

      {/* Week label + rollover info */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '16px', color: '#374151', fontWeight: '500' }}>
          {formatDate(dayDates[0])} &mdash; {formatDate(dayDates[4])}
        </div>
        {schedule.rolled_over > 0 && (
          <div style={{ fontSize: '13px', color: '#ea580c', fontWeight: '500' }}>
            {schedule.rolled_over} job{schedule.rolled_over !== 1 ? 's' : ''} rolled forward to today
          </div>
        )}
      </div>

      {/* Crew conflicts warning */}
      {schedule.conflicts && schedule.conflicts.length > 0 && (
        <div style={{
          backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px',
          padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#dc2626'
        }}>
          <strong>Crew Conflicts:</strong>
          {schedule.conflicts.map((c, i) => (
            <div key={i} style={{ marginTop: '4px' }}>
              {c.member} is in two places at {formatTime(c.time)} on {formatDate(c.date)}: {c.jobs.join(', ')}
            </div>
          ))}
        </div>
      )}

      {/* Mobile view */}
      {isMobile ? (
        <div>
          {/* Day tabs */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', overflowX: 'auto' }}>
            {dayDates.map((date, i) => {
              const dayJobs = schedule.scheduled[date] || [];
              const isToday = date === today;
              return (
                <button
                  key={date}
                  onClick={() => setMobileDay(i)}
                  style={{
                    padding: '8px 12px', borderRadius: '6px', border: isToday ? '2px solid #2563eb' : 'none',
                    cursor: 'pointer', fontSize: '12px', fontWeight: '600', flexShrink: 0,
                    backgroundColor: mobileDay === i ? '#2563eb' : isToday ? '#eff6ff' : '#f5f5f5',
                    color: mobileDay === i ? 'white' : '#374151'
                  }}
                >
                  {DAY_SHORT[i]} {dayJobs.length > 0 && `(${dayJobs.length})`}
                </button>
              );
            })}
          </div>

          {/* Mobile content */}
          <div>
            <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>
              {DAY_NAMES[mobileDay]}, {formatDate(dayDates[mobileDay])}
            </h3>
            {((schedule.scheduled[dayDates[mobileDay]] || []).length === 0 && (schedule.scheduled_work_orders?.[dayDates[mobileDay]] || []).length === 0) ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                No jobs scheduled
              </div>
            ) : (
              <>
                {(schedule.scheduled[dayDates[mobileDay]] || []).map(job => (
                  <JobCard
                    key={job.id} job={job} isMobile
                    onPriorityChange={canEditSchedule ? handlePriorityChange : undefined}
                    onEnRoute={dayDates[mobileDay] === today ? handleEnRoute : undefined}
                    onPreview={setPreviewJobId}
                    crews={crews}
                    soloOptions={soloOptions}
                    onCrewChange={canEditSchedule ? handleCrewChange : undefined}
                  />
                ))}
                {(schedule.scheduled_work_orders?.[dayDates[mobileDay]] || []).map(wo => (
                  <Link key={`wo${wo.id}`} to={`/jobs/${wo.job_id}`}
                    style={{ display: 'block', textDecoration: 'none', marginBottom: 8, background: '#eef2ff', border: '1px solid #c7d2fe', borderLeft: '3px solid #6366f1', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase' }}>🧩 {wo.phase_name || 'Work order'}</div>
                    <div style={{ fontSize: 13.5, color: '#0f172a', fontWeight: 600, marginTop: 2 }}>{wo.description}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{wo.job_number}{wo.client_name ? ` · ${wo.client_name}` : ''}</div>
                    {dayDates[mobileDay] === today && wo.phase_id && (
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleWoEnRoute(wo); }}
                        style={{ marginTop: 8, width: '100%', padding: '8px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                      >
                        🚗 En Route — Notify Client
                      </button>
                    )}
                  </Link>
                ))}
              </>
            )}
          </div>
        </div>
      ) : (
        /* Desktop view: day columns only */
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            {dayDates.map((date, i) => {
              const dayJobs = schedule.scheduled[date] || [];
              const isToday = date === today;
              return (
                <div
                  key={date}
                  onDragOver={(e) => { e.preventDefault(); setDragOverDate(date); }}
                  onDragLeave={() => setDragOverDate(null)}
                  onDrop={(e) => { e.preventDefault(); handleDrop(date); }}
                  style={{
                    flex: 1, minWidth: '180px',
                    backgroundColor: dragOverDate === date ? '#dbeafe' : isToday ? '#eff6ff' : '#f9fafb',
                    borderRadius: '12px', padding: '12px',
                    border: dragOverDate === date ? '2px dashed #2563eb' : isToday ? '2px solid #2563eb' : '1px solid #e5e7eb',
                    maxHeight: 'calc(100vh - 200px)', overflowY: 'auto',
                    transition: 'background-color 0.15s, border 0.15s'
                  }}>
                  <div style={{
                    textAlign: 'center', marginBottom: '12px', paddingBottom: '8px',
                    borderBottom: isToday ? '2px solid #2563eb' : '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontWeight: '700', fontSize: '14px', color: isToday ? '#2563eb' : '#374151' }}>
                      {DAY_NAMES[i]}
                    </div>
                    <div style={{ fontSize: '12px', color: isToday ? '#2563eb' : '#6b7280' }}>
                      {formatDate(date)}
                    </div>
                    <div style={{
                      fontSize: '11px', fontWeight: '600', marginTop: '4px',
                      color: dayJobs.filter(j => FIELD_WORK_STATUSES_SET.has(j.status)).length >= (schedule.daily_capacity || 4) ? '#dc2626' : dayJobs.length > 0 ? '#374151' : '#9ca3af'
                    }}>
                      {dayJobs.length}/{schedule.daily_capacity || 4} jobs
                    </div>
                    {canEditSchedule && dayJobs.length > 0 && (
                      <button
                        onClick={() => setNearbyPickerDate(date)}
                        style={{
                          marginTop: '4px', padding: '2px 8px', fontSize: '10px',
                          backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0',
                          borderRadius: '4px', cursor: 'pointer', color: '#16a34a'
                        }}
                        title="Find nearby unscheduled jobs"
                      >
                        <FiNavigation size={10} /> Nearby
                      </button>
                    )}
                  </div>

                  {dayJobs.length === 0 ? (
                    <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '12px' }}>
                      No jobs
                    </div>
                  ) : dayJobs.map(job => (
                    <div key={job.id} style={{ position: 'relative' }}>
                      <JobCard
                        job={job}
                        onPriorityChange={canEditSchedule ? handlePriorityChange : undefined}
                        onDragStart={canEditSchedule ? setDraggingJobId : undefined}
                        onEnRoute={date === today ? handleEnRoute : undefined}
                        onPreview={setPreviewJobId}
                        crews={crews}
                        soloOptions={soloOptions}
                        onCrewChange={canEditSchedule ? handleCrewChange : undefined}
                      />
                      {canEditSchedule && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleUnschedule(job.id); }}
                          style={{
                            position: 'absolute', top: '4px', right: '4px',
                            padding: '2px', background: 'none', border: 'none',
                            cursor: 'pointer', color: '#9ca3af', fontSize: '14px'
                          }}
                          title="Remove from schedule"
                        >
                          <FiX size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                  {/* Scheduled work orders on their own date (phased jobs) */}
                  {(schedule.scheduled_work_orders?.[date] || []).map(wo => (
                    <Link key={`wo${wo.id}`} to={`/jobs/${wo.job_id}`}
                      draggable={canEditSchedule}
                      onDragStart={canEditSchedule ? () => setDraggingWO({ id: wo.id, job_id: wo.job_id }) : undefined}
                      style={{ display: 'block', textDecoration: 'none', marginTop: 6, background: '#eef2ff', border: '1px solid #c7d2fe', borderLeft: '3px solid #6366f1', borderRadius: 8, padding: '8px 10px', cursor: canEditSchedule ? 'grab' : 'pointer' }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                        🧩 {wo.phase_name || 'Work order'}
                      </div>
                      <div style={{ fontSize: 12.5, color: '#0f172a', fontWeight: 600, marginTop: 2 }}>{wo.description}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{wo.job_number}{wo.client_name ? ` · ${wo.client_name}` : ''}</div>
                      {date === today && wo.phase_id && (
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleWoEnRoute(wo); }}
                          style={{ marginTop: 8, width: '100%', padding: '6px 8px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}
                        >
                          🚗 En Route — Notify Client
                        </button>
                      )}
                    </Link>
                  ))}
                </div>
              );
            })}
        </div>
      )}

      {/* Schedule Modal */}
      {schedulingJob && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '400px', width: '100%'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}><FiCalendar /> Schedule Job</h3>
              <button onClick={() => setSchedulingJob(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={20} /></button>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontWeight: '600', fontSize: '14px', display: 'block', marginBottom: '6px' }}>Date</label>
              <input
                type="date"
                value={scheduleForm.date}
                onChange={(e) => setScheduleForm({ ...scheduleForm, date: e.target.value })}
                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontWeight: '600', fontSize: '14px', display: 'block', marginBottom: '6px' }}>Time (optional)</label>
              <input
                type="time"
                value={scheduleForm.time}
                onChange={(e) => setScheduleForm({ ...scheduleForm, time: e.target.value })}
                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', boxSizing: 'border-box' }}
              />
            </div>

            {/* Quick date buttons */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {dayDates.map((date, i) => (
                <button
                  key={date}
                  onClick={() => setScheduleForm({ ...scheduleForm, date })}
                  style={{
                    flex: 1, padding: '8px', fontSize: '12px', fontWeight: '600',
                    backgroundColor: scheduleForm.date === date ? '#2563eb' : '#f5f5f5',
                    color: scheduleForm.date === date ? 'white' : '#374151',
                    border: 'none', borderRadius: '6px', cursor: 'pointer'
                  }}
                >
                  {DAY_SHORT[i]}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSchedulingJob(null)}
                style={{ padding: '10px 20px', backgroundColor: '#f5f5f5', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleScheduleSubmit}
                disabled={!scheduleForm.date}
                style={{
                  padding: '10px 20px',
                  backgroundColor: scheduleForm.date ? '#2563eb' : '#ccc',
                  color: 'white', border: 'none', borderRadius: '6px',
                  cursor: scheduleForm.date ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', gap: '6px'
                }}
              >
                <FiArrowRight /> Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Nearby Job Picker - choose which job to measure from */}
      {nearbyPickerDate && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '420px', width: '100%'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}><FiNavigation /> Find Jobs Near...</h3>
              <button onClick={() => setNearbyPickerDate(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={20} /></button>
            </div>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              Which job do you want to find nearby work for?
            </p>
            {(schedule.scheduled[nearbyPickerDate] || []).map(job => (
              <button
                key={job.id}
                onClick={() => {
                  if (job.latitude && job.longitude) {
                    loadNearbyJobs(nearbyPickerDate, job.latitude, job.longitude);
                  } else {
                    loadNearbyJobs(nearbyPickerDate);
                  }
                }}
                style={{
                  width: '100%', padding: '12px', marginBottom: '8px', border: '1px solid #e5e7eb',
                  borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer', textAlign: 'left',
                  fontSize: '14px'
                }}
              >
                <strong>{job.job_number}</strong>
                {job.property_address && <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{job.property_address}</div>}
                {!job.latitude && <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '2px' }}>No coordinates — will use office location</div>}
              </button>
            ))}
            <button
              onClick={() => setNearbyPickerDate(null)}
              style={{
                width: '100%', padding: '10px', marginTop: '8px', backgroundColor: '#f5f5f5',
                border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#666'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Nearby Jobs Modal */}
      {showNearby && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '500px', width: '100%', maxHeight: '80vh', overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}><FiNavigation /> Nearby Unscheduled Jobs</h3>
              <button onClick={() => setShowNearby(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={20} /></button>
            </div>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              Field work jobs closest to {formatDate(showNearby)}'s work area. Click "+ Add" to move a job to this day.
            </p>
            {nearbyJobs.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                No nearby unscheduled jobs with coordinates found
              </div>
            ) : nearbyJobs.map(job => (
              <div key={job.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px', borderBottom: '1px solid #f0f0f0', fontSize: '13px'
              }}>
                <div style={{ flex: 1 }}>
                  <strong>{job.job_number}</strong>
                  {job.client_name && <span style={{ color: '#666' }}> — {job.client_name}</span>}
                  {job.property_address && <div style={{ fontSize: '12px', color: '#999' }}>{job.property_address}</div>}
                  <div style={{ fontSize: '11px', color: '#16a34a', fontWeight: '600' }}>
                    {job.distance_miles} mi away
                    {job.scheduled_date && <span style={{ color: '#6b7280', fontWeight: '400' }}> &middot; on {formatDate(job.scheduled_date)}</span>}
                  </div>
                </div>
                {canEditSchedule && (
                  <button
                    onClick={async () => {
                      await handleQuickSchedule(job.id, showNearby);
                      loadNearbyJobs(showNearby);
                    }}
                    style={{
                      padding: '6px 12px', fontSize: '12px', backgroundColor: '#eff6ff',
                      border: '1px solid #bfdbfe', borderRadius: '6px', cursor: 'pointer',
                      color: '#2563eb', fontWeight: '600', flexShrink: 0
                    }}
                  >
                    + Add
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {previewJobId && (
        <ScheduleJobPreviewModal
          jobId={previewJobId}
          onClose={() => setPreviewJobId(null)}
        />
      )}
    </div>
  );
}

export default Schedule;
